import React, { FC, useEffect, useState } from 'react'
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars'
import CN from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import {
  useGetInternalConsignmentOrders,
  useGetPartnerTable,
  useReportExport,
} from 'framework/api/methods'
import { Couriers } from 'static-data/couriers'

import { Button, SimpleSelect } from 'components/atoms'
import { dateToReadableString } from 'utils'
import { runExportWithToast } from 'utils/exportToast'

import { DataTable } from './DataTable'

import './datePicker.scss'

export interface ConsignmentOrdersReportDataTableProps {
  [x: string]: any
}

export const ConsignmentOrdersReportDataTable: FC<
  ConsignmentOrdersReportDataTableProps
> = ({ notify }: ConsignmentOrdersReportDataTableProps) => {
  const [rows, setRows] = useState({})
  const [tableVariables, setTableVariables] = useState({
    take: 10,
    skip: 0,
  })
  const date = new Date()
  const [pageNumber, setPageNumber] = useState(1)

  const [totalRowCount, setTotalRowCount] = useState(0)
  const [isFilterMenuOpen, setIsFilterModalOpen] = useState(false)
  const [filterActive, setFilterActive] = useState(false)
  const [filterVariables, setFilterVariables] = useState<any>({
    principle: { label: 'All', value: 'All' },
    courier: { label: 'All', value: 'All' },
    startDate: new Date(date.setDate(date.getDate() - 30)),
    endDate: new Date(),
  })
  const [principleList, setPrincipleList] = useState([])

  /** API Call for partner list using react-query */
  const { mutate: getPartnerListMutate } = useGetPartnerTable()

  /** Process the get partner list */
  async function getPartnerList() {
    getPartnerListMutate(
      { take: 100, skip: 0, Search: '' },
      {
        onSuccess: ({ data: successData }: any) => {
          const partners = successData.partners
            .map((principle: any) => {
              return {
                value: principle.code,
                label: principle.name,
              }
            })
            .sort((a: any, b: any) =>
              a.label.localeCompare(b.label, undefined, { numeric: true }),
            )
          setPrincipleList(partners)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData?.message || 'Error...', 'error')
        },
      },
    )
  }

  /** API Call for get list using react-query */
  const {
    mutate: getInternalConsignmentOrdersMutate,
    isLoading: getInternalConsignmentOrdersIsLoading,
  } = useGetInternalConsignmentOrders()

  /** Process the get table data */
  async function getInternalConsignmentOrders(
    take?: number,
    skip?: number,
    principle?: any,
    courier?: any,
    startDate?: any,
    endDate?: any,
  ) {
    getInternalConsignmentOrdersMutate(
      {
        take,
        skip,
        principle,
        courier,
        startDate: startDate
          ? startDate.getFullYear() +
            '-' +
            ('0' + (startDate.getMonth() + 1)).slice(-2) +
            '-' +
            startDate.getDate()
          : '',
        endDate: endDate
          ? endDate.getFullYear() +
            '-' +
            ('0' + (endDate.getMonth() + 1)).slice(-2) +
            '-' +
            endDate.getDate()
          : '',
      },
      {
        onSuccess: ({ data: successData }: any) => {
          setRows(successData?.reports || [])
          setTotalRowCount(successData?.totalCount)
        },
        onError: ({ response: { data: errData } }: any) => {
          //
        },
      },
    )
  }

  useEffect(() => {
    getInternalConsignmentOrders(
      tableVariables.take,
      tableVariables.skip,
      filterVariables.principle,
      filterVariables.courier,
      filterVariables.startDate,
      filterVariables.endDate,
    )
  }, [tableVariables])

  useEffect(() => {
    getPartnerList()
  }, [])

  /** API Call export pdf/csv using react-query */
  const { mutate: reportExportMutate } = useReportExport()

  /** Process the export csv/pdf*/
  const reportExport = (exportType: string) => {
    runExportWithToast({
      defaultFilename: `DispatchedOrderReport.${
        exportType === 'PDF' ? 'pdf' : 'csv'
      }`,
      trigger: ({ onSuccess, onError }) =>
        reportExportMutate(
          {
            exportType,
            reportType: 'Internal',
            module: 'DispatchedOrderReport',
            orderRequest: {
              principle:
                filterVariables?.principle?.value === 'All'
                  ? null
                  : filterVariables?.principle?.value,
              courier:
                filterVariables.courier?.value === 'All'
                  ? null
                  : filterVariables.courier?.value,
              DispatchedFrom: filterVariables.startDate
                ? filterVariables.startDate.getFullYear() +
                  '-' +
                  ('0' + (filterVariables.startDate.getMonth() + 1)).slice(-2) +
                  '-' +
                  ('0' + filterVariables.startDate.getDate()).slice(-2)
                : '',
              DispatchedTo: filterVariables.endDate
                ? filterVariables.endDate.getFullYear() +
                  '-' +
                  ('0' + (filterVariables.endDate.getMonth() + 1)).slice(-2) +
                  '-' +
                  ('0' + filterVariables.endDate.getDate()).slice(-2)
                : '',
            },
          },
          { onSuccess, onError },
        ),
    })
  }
  return (
    <div className='flex w-full flex-col justify-start items-center mt-10 h-full overflow-y-scroll'>
      <div className='flex justify-between items-center w-full mb-4'>
        <div className='flex items-center relative'>
          <Button
            onClick={() => {
              setIsFilterModalOpen(!isFilterMenuOpen)
            }}
            appearance={isFilterMenuOpen ? 'default' : 'outline'}>
            <i className='ri-filter-3-line pr-3'></i> Filter
          </Button>
          {filterActive && (
            <div className='w-[6px] h-[6px] bg-R-500 rounded-full absolute top-[0px] right-[-2px]'></div>
          )}
        </div>

        <div className='flex items-center gap-x-3'>
          {/*<Button
            appearance='dull'
            onClick={() => {
              reportExport('PDF')
            }}>
            Export PDF
          </Button>*/}
          <Button
            appearance='dull'
            onClick={() => {
              reportExport('CSV')
            }}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* expanding filter panel with open close animation */}
      <AnimatePresence initial={false}>
        {isFilterMenuOpen && (
          <motion.div
            className='flex flex-col bg-white w-full'
            initial='collapsed'
            animate='open'
            exit='collapsed'
            transition={{ type: 'tween', duration: 0.3 }}
            variants={{
              open: { opacity: 1, y: 0, height: 'auto' },
              collapsed: { opacity: -1, y: -35, height: 0 },
            }}>
            {/** Input field of filtering section */}
            <div className='flex flex-col w-full mb-4 py-2'>
              <div className='flex w-full gap-x-5'>
                <div className='flex flex-col w-1/3 z-[2]'>
                  <span className='pb-2 text-N-700'>By Principle</span>

                  <SimpleSelect
                    value={filterVariables?.principle}
                    onChange={(e: any) => {
                      setFilterVariables({
                        ...filterVariables,
                        principle: e,
                      })
                    }}
                    options={[{ label: 'All', value: 'All' }, ...principleList]}
                  />
                </div>

                <div className='flex flex-col w-1/3 z-[2]'>
                  <span className='pb-2 text-N-700'>By Courier</span>

                  <SimpleSelect
                    value={filterVariables?.courier}
                    onChange={(e: any) => {
                      setFilterVariables({
                        ...filterVariables,
                        courier: e,
                      })
                    }}
                    options={[{ label: 'All', value: 'All' }, ...Couriers]}
                  />
                </div>

                <div className='flex flex-col w-1/3 z-[2] order-date'>
                  <span className='pb-2 text-N-700'>Consignment Date</span>

                  <DateRangePickerComponent
                    format='dd/MM/yyyy'
                    delayUpdate={true}
                    placeholder='Select a range'
                    startDate={filterVariables?.startDate}
                    endDate={filterVariables?.endDate}
                    onChange={(e: any) => {
                      setFilterVariables({
                        ...filterVariables,
                        startDate: e.target.startDate,
                        endDate: e.target.endDate,
                      })
                    }}
                  />
                </div>
              </div>
              <div className='flex gap-x-4 mt-6'>
                <Button
                  onClick={() => {
                    const date = new Date()

                    filterVariables.principle.value === 'All' &&
                    filterVariables.courier.value === 'All' &&
                    dateToReadableString(filterVariables.startDate) ===
                      dateToReadableString(
                        new Date(date.setDate(date.getDate() - 30)),
                      ) &&
                    dateToReadableString(filterVariables.endDate) ===
                      dateToReadableString(new Date())
                      ? setFilterActive(false)
                      : setFilterActive(true)

                    getInternalConsignmentOrders(
                      tableVariables.take,
                      tableVariables.skip,
                      filterVariables.principle,
                      filterVariables.courier,
                      filterVariables.startDate,
                      filterVariables.endDate,
                    )
                    setIsFilterModalOpen(false)
                    setPageNumber(1)
                  }}>
                  Apply
                </Button>
                <Button
                  appearance='dull'
                  onClick={() => {
                    const date = new Date()
                    setFilterVariables({
                      principle: { label: 'All', value: 'All' },
                      courier: { label: 'All', value: 'All' },
                      startDate: new Date(date.setDate(date.getDate() - 30)),
                      endDate: new Date(),
                    })
                    setFilterActive(false)
                    setIsFilterModalOpen(false)
                    getInternalConsignmentOrders(
                      tableVariables.take,
                      tableVariables.skip,
                      { label: 'All', value: 'All' },
                      { label: 'All', value: 'All' },
                      new Date(new Date().setDate(new Date().getDate() - 30)),
                      new Date(),
                    )
                    setPageNumber(1)
                  }}>
                  Clear
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={CN(
          'styled-scroll overflow-auto w-full h-[calc(100%-55px)] bg-white',
        )}>
        <DataTable
          itemData={rows}
          setTableVariables={setTableVariables}
          tableVariables={tableVariables}
          totalRowCount={totalRowCount}
          getInternalConsignmentOrdersIsLoading={
            getInternalConsignmentOrdersIsLoading
          }
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
        />
      </div>
    </div>
  )
}

ConsignmentOrdersReportDataTable.defaultProps = {}

export default ConsignmentOrdersReportDataTable
